import React, { useState, useEffect, useCallback } from "react";
import { db, auth, storage } from "./firebase";
import {
  collection, getDocs, query, where, orderBy, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// ─── Colours & Design System ──────────────────────────────────────
// Navy + Gold institutional palette — completely different from NovaCert teal
const C = {
  navy:       "#0F1F3D",
  navyMid:    "#162544",
  navyLight:  "#1E3A5F",
  gold:       "#C9A84C",
  goldLight:  "#F5E6C0",
  goldBright: "#E5C06A",
  white:      "#FFFFFF",
  offWhite:   "#F8F7F4",
  cream:      "#FAF8F2",
  body:       "#374151",
  muted:      "#6B7280",
  border:     "#E5E0D5",
  teal:       "#0D9488",   // NovaCert teal — used only for CredSeal references
  purple:     "#7C3AED",
};

// ─── Fonts ──────────────────────────────────────────────────────
const fontLink = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700&display=swap";

// ─── Global Styles ──────────────────────────────────────────────
const globalStyles = `
@import url('${fontLink}');
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',sans-serif;background:${C.offWhite};color:${C.body};-webkit-font-smoothing:antialiased}
.cm-serif{font-family:'Cormorant Garamond',serif}
.cm-card{background:#fff;border-radius:4px;border:1px solid ${C.border};transition:box-shadow .25s}
.cm-card:hover{box-shadow:0 8px 32px rgba(15,31,61,.08)}
.cm-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;border-radius:3px;font-weight:600;font-size:14px;border:none;cursor:pointer;transition:all .2s;font-family:'Inter',sans-serif;letter-spacing:.3px}
.cm-btn-gold{background:${C.gold};color:#fff}.cm-btn-gold:hover{background:${C.goldBright};transform:translateY(-1px)}
.cm-btn-navy{background:${C.navy};color:#fff}.cm-btn-navy:hover{background:${C.navyLight}}
.cm-btn-outline{background:transparent;color:${C.navy};border:2px solid ${C.navy}}.cm-btn-outline:hover{background:${C.navy};color:#fff}
.cm-btn-outline-gold{background:transparent;color:${C.gold};border:2px solid ${C.gold}}.cm-btn-outline-gold:hover{background:${C.gold};color:#fff}
.cm-btn-sm{padding:8px 18px;font-size:13px}
.cm-input{width:100%;padding:12px 16px;border:1.5px solid ${C.border};border-radius:3px;font-size:14px;font-family:'Inter',sans-serif;transition:border-color .2s;background:#fff;color:${C.body};outline:none}
.cm-input:focus{border-color:${C.gold};box-shadow:0 0 0 3px ${C.goldLight}40}
.cm-input::placeholder{color:#9CA3AF}
.cm-label{display:block;font-weight:600;font-size:11px;color:${C.muted};margin-bottom:5px;text-transform:uppercase;letter-spacing:.8px}
.fade-in{animation:fadeIn .5s ease-out}
@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
.slide-up{animation:slideUp .7s ease-out}
@keyframes slideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
@keyframes spin{to{transform:rotate(360deg)}}
.gold-line{height:2px;background:linear-gradient(90deg,${C.gold},${C.goldBright},${C.gold})}
.cm-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:24px}
.cm-grid-2-wide{display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:start}
.cm-flex-wrap{display:flex;flex-wrap:wrap;gap:12px}
@media(max-width:768px){
  .hide-mobile{display:none !important}
  .cm-btn{padding:10px 18px;font-size:13px}
  .cm-grid-2{grid-template-columns:1fr !important}
  .cm-grid-2-wide{grid-template-columns:1fr !important;gap:24px !important}
  .cm-section-pad{padding:40px 16px !important}
  .cm-hero-pad{padding:56px 16px 40px !important}
  .cm-hide-mobile{display:none !important}
  .cm-full-mobile{width:100% !important;min-width:unset !important}
}
`;

// ─── Utility ────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return "—";
  const date = d.toDate ? d.toDate() : new Date(d);
  return date.toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
};
const isExpired = (d) => {
  if (!d) return false;
  const date = d.toDate ? d.toDate() : new Date(d);
  return date < new Date();
};
const PLANS = {
  professional: { name:"Professional Certification", badge:"NovaCert Verified", color: C.teal },
  credseal:     { name:"Advanced Certification + CredMark™", badge:"CredMark™ Advanced", color: C.purple },
};

// ─── Icons ──────────────────────────────────────────────────────
const Icons = {
  Shield:  ()=>(<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>),
  Search:  ()=>(<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>),
  Check:   ()=>(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>),
  X:       ()=>(<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>),
  Menu:    ()=>(<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>),
  Download:()=>(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>),
  Arrow:   ()=>(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>),
  Clock:   ()=>(<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>),
  Loader:  ()=>(<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{animation:"spin 1s linear infinite"}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>),
  Book:    ()=>(<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>),
  Globe:   ()=>(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>),
  Users:   ()=>(<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
  Star:    ()=>(<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>),
};

// ═══════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════
export default function App() {
  const [page, setPage] = useState("home");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(()=>{
    const unsub = onAuthStateChanged(auth, u=>{ setAdminUser(u); setAuthChecked(true); });
    return unsub;
  },[]);

  const navigate = (p) => { setPage(p); setMobileMenu(false); window.scrollTo?.(0,0); };

  const navLinks = [
    ["home","Home"],
    ["verify","Verify Certificate"],
    ["directory","Certified Directory"],
    ["standard","The Standard"],
    ["governance","Governance"],
    ["auditors","Become an Auditor"],
    ["contact","Contact"],
  ];

  const handleLogout = async () => { await signOut(auth); navigate("home"); };

  return (
    <>
      <style>{globalStyles}</style>

      {/* ── NAV ── */}
      <nav style={{position:"sticky",top:0,zIndex:100,background:"rgba(15,31,61,0.97)",backdropFilter:"blur(16px)",borderBottom:`1px solid ${C.navyLight}`}}>
        <div style={{maxWidth:1200,margin:"0 auto",padding:"0 16px",display:"flex",alignItems:"center",justifyContent:"space-between",height:68,minHeight:68}}>
          {/* Logo */}
          <div onClick={()=>navigate("home")} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:36,height:36,borderRadius:3,background:`linear-gradient(135deg,${C.gold},${C.goldBright})`,display:"flex",alignItems:"center",justifyContent:"center",color:C.navy,fontWeight:800,fontSize:16,fontFamily:"'Cormorant Garamond',serif"}}>C</div>
            <div>
              <span style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:22,color:C.white,letterSpacing:.5}}>CredMark</span>
              <span style={{color:C.gold,fontSize:14,fontWeight:700}}>™</span>
              <span style={{fontSize:8,display:"block",color:"rgba(255,255,255,.4)",letterSpacing:3,textTransform:"uppercase",marginTop:-3}}>Credibility Standard</span>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hide-mobile" style={{display:"flex",alignItems:"center",gap:4}}>
            {navLinks.map(([key,label])=>(
              <button key={key} onClick={()=>navigate(key)} style={{padding:"7px 13px",background:page===key?`rgba(201,168,76,.15)`:"transparent",color:page===key?C.gold:"rgba(255,255,255,.75)",border:`1px solid ${page===key?`rgba(201,168,76,.3)`:"transparent"}`,borderRadius:3,fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif",transition:"all .2s"}}>{label}</button>
            ))}
            {adminUser
              ? <button onClick={handleLogout} style={{marginLeft:8,padding:"7px 13px",background:"rgba(220,38,38,.15)",color:"#FCA5A5",border:"1px solid rgba(220,38,38,.3)",borderRadius:3,fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Logout</button>
              : <button onClick={()=>navigate("admin")} style={{marginLeft:8,padding:"7px 13px",background:"rgba(201,168,76,.1)",color:C.gold,border:`1px solid rgba(201,168,76,.2)`,borderRadius:3,fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>Admin</button>
            }
            <button className="cm-btn cm-btn-gold cm-btn-sm" onClick={()=>window.open("https://novacert.in","_blank")} style={{marginLeft:8}}>Apply via NovaCert <Icons.Arrow/></button>
          </div>

          {/* Mobile hamburger */}
          <button onClick={()=>setMobileMenu(!mobileMenu)} style={{display:"none",background:"none",border:"none",cursor:"pointer",color:C.white}} className="show-mobile">
            {mobileMenu ? <Icons.X/> : <Icons.Menu/>}
          </button>
        </div>
        <style>{`@media(max-width:768px){.show-mobile{display:block!important}}`}</style>
        {mobileMenu && (
          <div style={{background:C.navy,borderTop:`1px solid ${C.navyLight}`,padding:"12px 24px"}}>
            {navLinks.map(([key,label])=>(
              <button key={key} onClick={()=>navigate(key)} style={{display:"block",width:"100%",textAlign:"left",padding:"11px 0",background:"none",border:"none",borderBottom:`1px solid rgba(255,255,255,.06)`,fontSize:15,fontWeight:500,color:page===key?C.gold:"rgba(255,255,255,.8)",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>{label}</button>
            ))}
            <button className="cm-btn cm-btn-gold" style={{marginTop:12,width:"100%",justifyContent:"center"}} onClick={()=>window.open("https://novacert.in","_blank")}>Apply via NovaCert</button>
          </div>
        )}
        {/* Gold accent line */}
        <div className="gold-line"/>
      </nav>

      {/* ── PAGES ── */}
      <div className="fade-in" key={page}>
        {page==="home"       && <HomePage onNavigate={navigate}/>}
        {page==="verify"     && <VerifyPage/>}
        {page==="directory"  && <DirectoryPage onNavigate={navigate} onSelect={setSelectedBiz}/>}
        {page==="standard"   && <StandardPage/>}
        {page==="governance" && <GovernancePage/>}
        {page==="auditors"   && <AuditorsPage/>}
        {page==="contact"    && <ContactPage/>}
        {page==="biz"        && <BizDetailPage biz={selectedBiz} onBack={()=>navigate("directory")}/>}
        {page==="terms"      && <CredMarkTermsPage/>}
        {page==="admin"      && !adminUser && <AdminLogin authChecked={authChecked}/>}
        {page==="admin"      && adminUser  && <AdminPanel/>}
      </div>

      {/* ── FOOTER ── */}
      <footer style={{background:C.navy,color:"rgba(255,255,255,.55)",padding:"56px 24px 32px",marginTop:80}}>
        <div style={{maxWidth:1200,margin:"0 auto",display:"flex",flexWrap:"wrap",gap:40,justifyContent:"space-between"}}>
          <div style={{flex:"1 1 280px"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <div style={{width:30,height:30,borderRadius:3,background:`linear-gradient(135deg,${C.gold},${C.goldBright})`,display:"flex",alignItems:"center",justifyContent:"center",color:C.navy,fontWeight:800,fontSize:14,fontFamily:"'Cormorant Garamond',serif"}}>C</div>
              <span style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:20,color:C.white}}>CredMark™</span>
            </div>
            <p style={{fontSize:13,lineHeight:1.8,maxWidth:280,color:"rgba(255,255,255,.5)"}}>The credibility certification standard for Indian service businesses. Earned, not bought.</p>
            <p style={{fontSize:12,marginTop:12,color:C.gold}}>Administered by NovaCert</p>
          </div>
          <div>
            <h4 style={{color:C.gold,fontSize:11,fontWeight:700,marginBottom:14,textTransform:"uppercase",letterSpacing:1.5}}>The Standard</h4>
            {[["Verify Certificate","verify"],["Certified Directory","directory"],["Download Manual","standard"],["Governance","governance"]].map(([l,k])=>(
              <div key={l} onClick={()=>navigate(k)} style={{fontSize:13,padding:"4px 0",cursor:"pointer",color:"rgba(255,255,255,.55)",transition:"color .2s"}} onMouseEnter={e=>e.target.style.color=C.gold} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.55)"}>{l}</div>
            ))}
          </div>
          <div>
            <h4 style={{color:C.gold,fontSize:11,fontWeight:700,marginBottom:14,textTransform:"uppercase",letterSpacing:1.5}}>Get Involved</h4>
            {[["Become an Auditor","auditors"],["Apply for Certification","home"],["Contact Us","contact"]].map(([l,k])=>(
              <div key={l} onClick={()=>navigate(k)} style={{fontSize:13,padding:"4px 0",cursor:"pointer",color:"rgba(255,255,255,.55)",transition:"color .2s"}} onMouseEnter={e=>e.target.style.color=C.gold} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.55)"}>{l}</div>
            ))}
            <div style={{marginTop:16}}>
              <a href="https://novacert.in" target="_blank" rel="noopener noreferrer" style={{fontSize:13,color:C.gold,textDecoration:"none",display:"flex",alignItems:"center",gap:6}}><Icons.Globe/> novacert.in</a>
            </div>
          </div>
          <div>
            <h4 style={{color:C.gold,fontSize:11,fontWeight:700,marginBottom:14,textTransform:"uppercase",letterSpacing:1.5}}>Contact</h4>
            <p style={{fontSize:13,color:"rgba(255,255,255,.55)",marginBottom:6}}>standards@credmark.org</p>
            <p style={{fontSize:13,color:"rgba(255,255,255,.55)",marginBottom:6}}>verify@credmark.org</p>
            <p style={{fontSize:13,color:"rgba(255,255,255,.55)"}}>+91 91150 19666</p>
          </div>
        </div>
        <div style={{maxWidth:1200,margin:"32px auto 0",paddingTop:24,borderTop:`1px solid rgba(255,255,255,.08)`,display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8,fontSize:12,color:"rgba(255,255,255,.3)"}}>
          <span>© {new Date().getFullYear()} CredMark™. All rights reserved. CredMark™ is a proprietary standard owned by NovaCert.</span>
          <div style={{display:"flex",gap:16}}>
            <span onClick={()=>navigate("terms")} style={{cursor:"pointer",color:"rgba(255,255,255,.4)",transition:"color .2s"}} onMouseEnter={e=>e.target.style.color=C.gold} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,.4)"}>Terms of Use</span>
            <span>credmark.org</span>
          </div>
        </div>
      </footer>
    </>
  );
}

// ─── Section wrapper ────────────────────────────────────────────
function Section({ children, style={} }) {
  return <section className="cm-section-pad" style={{maxWidth:1100,margin:"0 auto",padding:"64px 24px",...style}}>{children}</section>;
}

// ─── Page header ────────────────────────────────────────────────
function PageHeader({ label, title, subtitle, dark=false }) {
  return (
    <div style={{textAlign:"center",marginBottom:52,padding:"56px 24px 0"}}>
      {label && <div style={{display:"inline-block",background:dark?`rgba(201,168,76,.15)`:`${C.goldLight}`,border:`1px solid ${dark?"rgba(201,168,76,.3)":C.gold+"40"}`,color:C.gold,fontSize:11,fontWeight:700,padding:"4px 14px",borderRadius:20,letterSpacing:1.5,textTransform:"uppercase",marginBottom:16}}>{label}</div>}
      <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,46px)",fontWeight:700,color:dark?C.white:C.navy,marginBottom:14,lineHeight:1.15}}>{title}</h1>
      {subtitle && <p style={{color:dark?"rgba(255,255,255,.6)":C.muted,fontSize:16,maxWidth:580,margin:"0 auto",lineHeight:1.7}}>{subtitle}</p>}
      <div style={{width:48,height:2,background:`linear-gradient(90deg,${C.gold},${C.goldBright})`,margin:"20px auto 0",borderRadius:1}}/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HOME PAGE
// ═══════════════════════════════════════════════════════════════
function HomePage({ onNavigate }) {
  const [stats, setStats] = useState({ total:0, credseal:0, professional:0 });

  useEffect(()=>{
    getDocs(query(collection(db,"applications"),where("status","==","approved"))).then(snap=>{
      const apps = snap.docs.map(d=>d.data());
      setStats({
        total: apps.filter(a=>!isExpired(a.expiryDate)).length,
        credseal: apps.filter(a=>a.plan==="credseal"&&!isExpired(a.expiryDate)).length,
        professional: apps.filter(a=>a.plan==="professional"&&!isExpired(a.expiryDate)).length,
      });
    }).catch(()=>{});
  },[]);

  return (
    <>
      {/* Hero */}
      <section style={{background:`linear-gradient(160deg,${C.navy} 0%,${C.navyMid} 60%,${C.navyLight} 100%)`,padding:"clamp(56px,10vw,100px) 16px clamp(48px,8vw,80px)",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-80,right:-80,width:500,height:500,borderRadius:"50%",background:`radial-gradient(circle,rgba(201,168,76,.06) 0%,transparent 70%)`}}/>
        <div style={{position:"absolute",bottom:-60,left:-60,width:360,height:360,borderRadius:"50%",background:`radial-gradient(circle,rgba(201,168,76,.04) 0%,transparent 70%)`}}/>
        <div style={{maxWidth:900,margin:"0 auto",textAlign:"center",position:"relative"}} className="slide-up">
          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:`rgba(201,168,76,.12)`,border:`1px solid rgba(201,168,76,.25)`,padding:"5px 16px",borderRadius:20,fontSize:12,color:C.gold,marginBottom:28,fontWeight:600,letterSpacing:.5}}>
            <Icons.Shield/> THE INDEPENDENT CREDIBILITY STANDARD
          </div>
          <h1 className="cm-serif" style={{fontSize:"clamp(38px,5.5vw,72px)",fontWeight:700,lineHeight:1.05,marginBottom:22,color:C.white}}>
            Trust is Earned.<br/>
            <span style={{color:C.gold}}>Now It Can Be Verified.</span>
          </h1>
          <p style={{fontSize:18,color:"rgba(255,255,255,.65)",maxWidth:600,margin:"0 auto 40px",lineHeight:1.75}}>
            CredMark™ is an independent certification standard that defines what a genuinely credible business looks like — and independently verifies it.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="cm-btn cm-btn-gold" onClick={()=>onNavigate("verify")} style={{fontSize:15,padding:"15px 32px"}}>Verify a Certificate <Icons.Search/></button>
            <button className="cm-btn cm-btn-outline-gold" onClick={()=>onNavigate("standard")} style={{fontSize:15,padding:"15px 32px"}}>Read the Standard <Icons.Book/></button>
          </div>
        </div>

        {/* Stats */}
        <div style={{maxWidth:700,margin:"60px auto 0",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:1,background:`rgba(201,168,76,.15)`,borderRadius:4,overflow:"hidden",border:`1px solid rgba(201,168,76,.2)`}} className="cm-stats-strip">
          <style>{`@media(max-width:500px){.cm-stats-strip{grid-template-columns:1fr !important}}`}</style>
          {[
            [stats.total,"Certified Businesses","Active today"],
            [stats.credseal,"CredMark™ Advanced","Premium tier"],
            [stats.professional,"Professional","Entry tier"],
          ].map(([n,label,sub])=>(
            <div key={label} style={{padding:"24px 20px",textAlign:"center",background:`rgba(15,31,61,.6)`}}>
              <div className="cm-serif" style={{fontSize:40,fontWeight:700,color:C.gold,lineHeight:1}}>{n}</div>
              <div style={{fontSize:13,fontWeight:600,color:C.white,marginTop:4}}>{label}</div>
              <div style={{fontSize:11,color:"rgba(255,255,255,.4)",marginTop:2}}>{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What is CredMark */}
      <Section>
        <div className="cm-grid-2-wide" style={{gap:64}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>About the Standard</div>
            <h2 className="cm-serif" style={{fontSize:"clamp(26px,3.5vw,42px)",fontWeight:700,color:C.navy,marginBottom:20,lineHeight:1.2}}>What is CredMark™?</h2>
            <p style={{color:C.body,fontSize:15,lineHeight:1.8,marginBottom:16}}>CredMark™ is a proprietary credibility certification standard developed, owned, and administered by NovaCert. It defines the criteria, methodology, and process by which a business is independently evaluated and certified as credible.</p>
            <p style={{color:C.muted,fontSize:14,lineHeight:1.8,marginBottom:28}}>It is not a membership. It is not a subscription. It cannot be purchased. It is earned through a rigorous, independent 40–45 day audit — or not at all.</p>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              <button className="cm-btn cm-btn-navy" onClick={()=>onNavigate("standard")}>Download the Standard</button>
              <button className="cm-btn cm-btn-outline" onClick={()=>onNavigate("verify")}>Verify a Certificate</button>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {[
              {icon:"❌",title:"Not a membership",desc:"You cannot pay to join. You must qualify."},
              {icon:"❌",title:"Not self-reported",desc:"All claims are independently verified by auditors."},
              {icon:"❌",title:"Not permanent",desc:"Certificates expire annually and require renewal."},
              {icon:"✅",title:"Evidence-based",desc:"Every score is backed by documented proof."},
              {icon:"✅",title:"Publicly verifiable",desc:"Anyone can check any certificate at credmark.org."},
            ].map(item=>(
              <div key={item.title} style={{display:"flex",gap:14,alignItems:"flex-start",padding:"14px 18px",background:item.icon==="✅"?`${C.goldLight}40`:"#FEF2F2",borderRadius:4,border:`1px solid ${item.icon==="✅"?`${C.gold}30`:"#FECACA"}`}}>
                <span style={{fontSize:16,flexShrink:0}}>{item.icon}</span>
                <div>
                  <p style={{fontWeight:600,fontSize:13,color:C.navy,marginBottom:2}}>{item.title}</p>
                  <p style={{fontSize:12,color:C.muted}}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Five Pillars */}
      <section style={{background:C.cream,padding:"64px 24px",borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:48}}>
            <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:10}}>The Framework</div>
            <h2 className="cm-serif" style={{fontSize:"clamp(24px,3vw,38px)",fontWeight:700,color:C.navy}}>The Five Pillars of Credibility</h2>
            <p style={{color:C.muted,fontSize:15,marginTop:10,maxWidth:520,margin:"10px auto 0"}}>Every CredMark™ evaluation is structured around five foundational pillars</p>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:16}}>
            {[
              {n:"01",title:"Business Credibility",desc:"Verified track record and documented history"},
              {n:"02",title:"Ethical Practices",desc:"Transparent, honest, fair client dealings"},
              {n:"03",title:"Operational Integrity",desc:"Legal compliance and proper documentation"},
              {n:"04",title:"Client Trust",desc:"Independently verified client satisfaction"},
              {n:"05",title:"Professional Conduct",desc:"Standards of presentation and communication"},
            ].map(p=>(
              <div key={p.n} className="cm-card" style={{padding:"28px 22px",background:"#fff"}}>
                <div className="cm-serif" style={{fontSize:36,fontWeight:700,color:`${C.gold}40`,marginBottom:8,lineHeight:1}}>{p.n}</div>
                <h3 style={{fontSize:15,fontWeight:700,color:C.navy,marginBottom:8}}>{p.title}</h3>
                <p style={{fontSize:13,color:C.muted,lineHeight:1.6}}>{p.desc}</p>
              </div>
            ))}
          </div>
          <div style={{textAlign:"center",marginTop:32}}>
            <button className="cm-btn cm-btn-outline" onClick={()=>onNavigate("standard")}>Read Full Criteria <Icons.Arrow/></button>
          </div>
        </div>
      </section>

      {/* For Businesses & Clients */}
      <Section>
        <div className="cm-grid-2" style={{gap:24}}>
          {[
            {
              icon:"🏢", title:"For Businesses",
              desc:"Get your business independently verified and earn the CredMark™ badge. Show clients that you have been audited — not just self-certified.",
              points:["Apply online at NovaCert.in","40–45 day independent audit","Certificate valid for 1 year","Publicly verifiable certificate"],
              cta:"Apply for Certification", link:"https://novacert.in", external:true,
            },
            {
              icon:"🔍", title:"For Clients & Verifiers",
              desc:"Before engaging a service provider, verify their CredMark™ certificate. Know exactly what has been checked and what it means.",
              points:["Enter certificate number","Instantly see full details","Check expiry and status","Find certified businesses"],
              cta:"Verify a Certificate", link:"verify", external:false,
            },
          ].map(card=>(
            <div key={card.title} className="cm-card" style={{padding:"36px 32px"}}>
              <div style={{fontSize:36,marginBottom:16}}>{card.icon}</div>
              <h3 className="cm-serif" style={{fontSize:24,fontWeight:700,color:C.navy,marginBottom:12}}>{card.title}</h3>
              <p style={{fontSize:14,color:C.muted,lineHeight:1.7,marginBottom:20}}>{card.desc}</p>
              {card.points.map(pt=>(
                <div key={pt} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,fontSize:13,color:C.body}}>
                  <span style={{color:C.gold,flexShrink:0}}><Icons.Check/></span>{pt}
                </div>
              ))}
              <button className="cm-btn cm-btn-gold" style={{marginTop:20}} onClick={()=>card.external?window.open(card.link,"_blank"):onNavigate(card.link)}>
                {card.cta} <Icons.Arrow/>
              </button>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA Banner */}
      <section style={{background:`linear-gradient(135deg,${C.navy},${C.navyLight})`,padding:"64px 24px",textAlign:"center"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{width:48,height:48,borderRadius:3,background:`linear-gradient(135deg,${C.gold},${C.goldBright})`,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",color:C.navy}}><Icons.Shield/></div>
          <h2 className="cm-serif" style={{fontSize:"clamp(24px,3.5vw,40px)",fontWeight:700,color:C.white,marginBottom:14}}>Is a Business Claiming CredMark™ Certification?</h2>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:16,marginBottom:32,lineHeight:1.7}}>Verify their certificate in seconds. Enter the certificate number and instantly confirm it is genuine, valid, and current.</p>
          <button className="cm-btn cm-btn-gold" onClick={()=>onNavigate("verify")} style={{fontSize:16,padding:"15px 36px"}}>Verify Now <Icons.Search/></button>
        </div>
      </section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// VERIFY PAGE
// ═══════════════════════════════════════════════════════════════
function VerifyPage() {
  const [query2, setQuery2] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const q = query2.trim().toUpperCase();
    if (!q) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db,"applications"), where("certNumber","==",q))
      );
      setResult(snap.empty ? "not_found" : { id: snap.docs[0].id, ...snap.docs[0].data() });
    } catch(err) { setResult("error"); }
    setLoading(false);
  };

  return (
    <>
      <section style={{background:`linear-gradient(160deg,${C.navy},${C.navyLight})`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)",textAlign:"center"}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Certificate Verification</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.white,marginBottom:14}}>Verify a CredMark™ Certificate</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:15,marginBottom:36}}>Enter the certificate number to instantly confirm its authenticity and current status.</p>
          <div style={{display:"flex",gap:10,maxWidth:540,margin:"0 auto",flexWrap:"wrap"}}>
            <input className="cm-input" value={query2} onChange={e=>setQuery2(e.target.value)} placeholder="e.g. NC-PC-2026-AB1234" onKeyDown={e=>e.key==="Enter"&&search()} style={{flex:"1 1 200px",fontSize:15,background:"rgba(255,255,255,.95)"}}/>
            <button className="cm-btn cm-btn-gold cm-full-mobile" onClick={search} disabled={loading} style={{flexShrink:0,justifyContent:"center"}}>
              {loading?<Icons.Loader/>:<Icons.Search/>} Verify
            </button>
          </div>
          <p style={{color:"rgba(255,255,255,.35)",fontSize:12,marginTop:12}}>The certificate number is shown on the business's badge or certificate document</p>
        </div>
      </section>

      <Section style={{paddingTop:40}}>
        {result && result !== "not_found" && result !== "error" && (()=>{
          const expired = isExpired(result.expiryDate);
          const planInfo = PLANS[result.plan];
          return (
            <div style={{maxWidth:600,margin:"0 auto"}} className="fade-in">
              {/* Status banner */}
              <div style={{background:expired?`#FEF3C7`:`${C.goldLight}`,border:`2px solid ${expired?`#F59E0B`:C.gold}`,borderRadius:4,padding:"20px 24px",marginBottom:24,display:"flex",alignItems:"center",gap:14}}>
                <div style={{width:44,height:44,borderRadius:"50%",background:expired?"#F59E0B":C.gold,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#fff",fontSize:20}}>
                  {expired?"⚠":"✓"}
                </div>
                <div>
                  <h3 style={{fontSize:18,fontWeight:700,color:expired?"#92400E":C.navy}}>{expired?"Certificate Expired":"Certificate Valid & Active"}</h3>
                  <p style={{fontSize:13,color:C.muted,marginTop:2}}>Certificate #{result.certNumber}</p>
                </div>
              </div>

              {/* Details */}
              <div className="cm-card" style={{padding:"28px 32px"}}>
                <h3 style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:1.5,marginBottom:20}}>Certificate Details</h3>
                <div className="cm-grid-2" style={{gap:"14px 24px",fontSize:14}}>
                  {[
                    ["Business","companyName"],["Certificate Holder","fullName"],
                    ["Industry","industry"],["Location","city"],
                  ].map(([label,field])=>(
                    <div key={label}>
                      <p style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>{label}</p>
                      <p style={{fontWeight:600,color:C.navy}}>{result[field]}{field==="city"&&result.state?`, ${result.state}`:""}</p>
                    </div>
                  ))}
                  <div>
                    <p style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>Certification Tier</p>
                    <p style={{fontWeight:600,color:planInfo?.color}}>{planInfo?.badge}</p>
                  </div>
                  <div>
                    <p style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>Issued By</p>
                    <p style={{fontWeight:600,color:C.navy}}>NovaCert</p>
                  </div>
                  <div>
                    <p style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>Issue Date</p>
                    <p style={{fontWeight:600,color:C.navy}}>{formatDate(result.issuedAt)}</p>
                  </div>
                  <div>
                    <p style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>Valid Until</p>
                    <p style={{fontWeight:600,color:expired?"#DC2626":"#059669"}}>{formatDate(result.expiryDate)}{expired?" — EXPIRED":""}</p>
                  </div>
                </div>

                {result.plan==="credseal"&&!expired&&(
                  <div style={{marginTop:20,background:`${C.purple}10`,border:`1px solid ${C.purple}30`,borderRadius:4,padding:"10px 16px",display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.purple,fontWeight:600}}>
                    <Icons.Star/> CredMark™ Advanced — Highest tier of credibility verification
                  </div>
                )}

                {expired&&(
                  <div style={{marginTop:20,background:"#FEF3C7",border:"1px solid #F59E0B",borderRadius:4,padding:"12px 16px",fontSize:13,color:"#92400E"}}>
                    ⚠ This certificate has expired. The business may have renewed — ask them for their current certificate number.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {result==="not_found" && (
          <div style={{maxWidth:500,margin:"0 auto",textAlign:"center",padding:"40px 24px"}} className="fade-in">
            <div style={{fontSize:56,marginBottom:16}}>🔍</div>
            <h3 className="cm-serif" style={{fontSize:26,fontWeight:700,color:C.navy,marginBottom:8}}>Certificate Not Found</h3>
            <p style={{color:C.muted,fontSize:15,lineHeight:1.7,marginBottom:20}}>No certificate matches this number in the CredMark™ registry. Please double-check the number and try again.</p>
            <div style={{background:`${C.goldLight}`,border:`1px solid ${C.gold}40`,borderRadius:4,padding:"14px 18px",fontSize:13,color:C.navy,textAlign:"left"}}>
              <strong>If you believe this is an error:</strong> Contact us at verify@credmark.org with the certificate number and the business name.
            </div>
          </div>
        )}

        {result==="error" && (
          <div style={{maxWidth:500,margin:"0 auto",textAlign:"center",padding:"40px 24px"}}>
            <p style={{color:"#DC2626",fontSize:15}}>Verification failed. Please try again or contact verify@credmark.org</p>
          </div>
        )}

        {!result && (
          <div style={{textAlign:"center",padding:"20px 0",color:C.muted}}>
            <div style={{fontSize:48,marginBottom:12}}>⚖️</div>
            <p style={{fontSize:15}}>Enter a certificate number above to verify</p>
          </div>
        )}
      </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// CERTIFIED DIRECTORY PAGE
// ═══════════════════════════════════════════════════════════════
function DirectoryPage({ onNavigate, onSelect }) {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("all");

  useEffect(()=>{
    const q = query(collection(db,"applications"),where("status","==","approved"),orderBy("issuedAt","desc"));
    const unsub = onSnapshot(q, snap=>{
      setBusinesses(snap.docs.map(d=>({id:d.id,...d.data()})));
      setLoading(false);
    },()=>setLoading(false));
    return unsub;
  },[]);

  const active = businesses.filter(b=>!isExpired(b.expiryDate));
  const industries = ["all",...Array.from(new Set(active.map(b=>b.industry).filter(Boolean))).sort()];
  const filtered = active.filter(b=>{
    const ms = !search || b.companyName?.toLowerCase().includes(search.toLowerCase()) || b.city?.toLowerCase().includes(search.toLowerCase()) || b.industry?.toLowerCase().includes(search.toLowerCase());
    const mp = filterPlan==="all"||b.plan===filterPlan;
    const mi = filterIndustry==="all"||b.industry===filterIndustry;
    return ms&&mp&&mi;
  });

  return (
    <>
      <section style={{background:`linear-gradient(160deg,${C.navy},${C.navyLight})`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)"}}>
        <div style={{maxWidth:900,margin:"0 auto",textAlign:"center"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Verified Businesses</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.white,marginBottom:14}}>CredMark™ Certified Directory</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:15,marginBottom:36}}>Every business listed here has passed an independent CredMark™ audit and holds a valid, current certificate.</p>
          <div style={{display:"inline-flex",gap:32,padding:"20px 40px",background:`rgba(201,168,76,.1)`,border:`1px solid rgba(201,168,76,.2)`,borderRadius:4}}>
            {[[active.length,"Active Certified"],[businesses.filter(b=>b.plan==="credseal"&&!isExpired(b.expiryDate)).length,"CredMark™ Advanced"],[businesses.filter(b=>b.plan==="professional"&&!isExpired(b.expiryDate)).length,"Professional"]].map(([n,l])=>(
              <div key={l} style={{textAlign:"center"}}>
                <div className="cm-serif" style={{fontSize:36,fontWeight:700,color:C.gold,lineHeight:1}}>{n}</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,.6)",marginTop:4}}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section style={{paddingTop:40}}>
        {/* Filters */}
        <div style={{display:"flex",gap:12,marginBottom:24,flexWrap:"wrap"}}>
          <div style={{flex:"1 1 200px",minWidth:180,position:"relative"}}>
            <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",color:C.muted,pointerEvents:"none"}}><Icons.Search/></span>
            <input className="cm-input" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, city, industry..." style={{paddingLeft:42}}/>
          </div>
          <select className="cm-input cm-full-mobile" value={filterPlan} onChange={e=>setFilterPlan(e.target.value)} style={{width:"auto",cursor:"pointer",minWidth:160}}>
            <option value="all">All Tiers</option>
            <option value="professional">Professional</option>
            <option value="credseal">CredMark™ Advanced</option>
          </select>
          <select className="cm-input cm-full-mobile" value={filterIndustry} onChange={e=>setFilterIndustry(e.target.value)} style={{width:"auto",cursor:"pointer",minWidth:160}}>
            <option value="all">All Industries</option>
            {industries.filter(i=>i!=="all").map(i=><option key={i} value={i}>{i}</option>)}
          </select>
        </div>

        {!loading && <p style={{fontSize:13,color:C.muted,marginBottom:20}}>Showing <strong>{filtered.length}</strong> certified {filtered.length===1?"business":"businesses"}{search?` for "${search}`:""}</p>}

        {loading ? (
          <div style={{textAlign:"center",padding:80}}><Icons.Loader/></div>
        ) : filtered.length===0 ? (
          <div style={{textAlign:"center",padding:80}}>
            <div style={{fontSize:48,marginBottom:12}}>🔍</div>
            <p style={{color:C.muted,fontSize:15}}>No certified businesses match your search.</p>
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:20}}>
            {filtered.map(biz=>{
              const planInfo = PLANS[biz.plan];
              const isCS = biz.plan==="credseal";
              return (
                <div key={biz.id} className="cm-card" style={{padding:24,cursor:"pointer",position:"relative",border:isCS?`2px solid ${C.gold}40`:undefined}} onClick={()=>{onSelect(biz);onNavigate("biz");}}>
                  {isCS&&<div style={{position:"absolute",top:14,right:14,background:`linear-gradient(135deg,${C.gold},${C.goldBright})`,color:C.navy,fontSize:9,fontWeight:800,padding:"3px 10px",borderRadius:20,letterSpacing:.5,textTransform:"uppercase"}}>CredMark™</div>}
                  <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
                    <div style={{width:48,height:48,borderRadius:3,background:`${isCS?C.goldLight:`${C.teal}15`}`,color:isCS?C.gold:C.teal,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:18,flexShrink:0,fontFamily:"'Cormorant Garamond',serif"}}>
                      {biz.companyName?.[0]?.toUpperCase()||"B"}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <h3 style={{fontSize:15,fontWeight:700,color:C.navy,marginBottom:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{biz.companyName}</h3>
                      <p style={{fontSize:12,color:C.muted}}>{biz.city}{biz.state?`, ${biz.state}`:""}</p>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,fontSize:12,marginBottom:14}}>
                    <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:C.muted}}>Industry</span><span style={{fontWeight:600,color:C.navy}}>{biz.industry||"—"}</span></div>
                    <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:C.muted}}>Certificate</span><code style={{fontSize:10,background:C.cream,padding:"2px 6px",borderRadius:2,color:C.navy,border:`1px solid ${C.border}`}}>{biz.certNumber}</code></div>
                    <div style={{display:"flex",justifyContent:"space-between"}}><span style={{color:C.muted}}>Valid Until</span><span style={{fontWeight:600,color:"#059669"}}>{formatDate(biz.expiryDate)}</span></div>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:"#059669",fontWeight:600}}>
                      <span style={{width:6,height:6,borderRadius:"50%",background:"#059669",display:"inline-block"}}/>Active
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:planInfo?.color}}>{planInfo?.badge}</span>
                  </div>
                  {biz.website&&<a href={biz.website} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{display:"block",marginTop:10,fontSize:12,color:C.teal,textDecoration:"none",textAlign:"center",padding:"6px",background:`${C.teal}08`,borderRadius:3,border:`1px solid ${C.teal}20`}}>🌐 Visit Website</a>}
                </div>
              );
            })}
          </div>
        )}
      </Section>
    </>
  );
}

// ─── Business Detail Page ────────────────────────────────────
function BizDetailPage({ biz, onBack }) {
  if (!biz) { onBack(); return null; }
  const planInfo = PLANS[biz.plan];
  const expired = isExpired(biz.expiryDate);
  return (
    <Section style={{paddingTop:40}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:14,fontFamily:"'Inter',sans-serif",marginBottom:24,display:"flex",alignItems:"center",gap:6}}>← Back to Directory</button>
      <div style={{maxWidth:640,margin:"0 auto"}}>
        <div style={{display:"flex",gap:16,alignItems:"center",marginBottom:28}}>
          <div style={{width:64,height:64,borderRadius:4,background:`${C.goldLight}`,color:C.gold,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:28,fontFamily:"'Cormorant Garamond',serif"}}>{biz.companyName?.[0]?.toUpperCase()}</div>
          <div>
            <h1 className="cm-serif" style={{fontSize:32,fontWeight:700,color:C.navy}}>{biz.companyName}</h1>
            <p style={{fontSize:14,color:C.muted}}>{biz.industry} · {biz.city}{biz.state?`, ${biz.state}`:""}</p>
          </div>
        </div>
        <div className="cm-card" style={{padding:"28px 32px",marginBottom:20}}>
        <div className="cm-grid-2" style={{gap:"16px 24px",fontSize:14}}>
            {[["Certificate Number",biz.certNumber],["Certification Tier",planInfo?.badge],["Plan",planInfo?.name],["Holder",biz.fullName],["Issued",formatDate(biz.issuedAt)],["Valid Until",formatDate(biz.expiryDate)]].map(([l,v])=>(
              <div key={l}>
                <p style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>{l}</p>
                <p style={{fontWeight:600,color:l==="Valid Until"?(expired?"#DC2626":"#059669"):C.navy}}>{v}</p>
              </div>
            ))}
          </div>
        </div>
        <div style={{background:expired?"#FEF2F2":`${C.goldLight}`,border:`2px solid ${expired?"#FCA5A5":C.gold}`,borderRadius:4,padding:"16px 20px",display:"flex",alignItems:"center",gap:12,fontSize:14}}>
          <span style={{fontSize:22}}>{expired?"⚠":"✅"}</span>
          <div>
            <p style={{fontWeight:700,color:expired?"#DC2626":C.navy}}>{expired?"Certificate Expired":"Certificate Valid & Active"}</p>
            <p style={{fontSize:12,color:C.muted,marginTop:2}}>Verified in the CredMark™ registry</p>
          </div>
        </div>
        {biz.website&&<a href={biz.website} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:16,padding:"12px",background:C.cream,border:`1px solid ${C.border}`,borderRadius:4,fontSize:14,color:C.teal,textDecoration:"none",fontWeight:600}}><Icons.Globe/> Visit Website</a>}
      </div>
    </Section>
  );
}

// ═══════════════════════════════════════════════════════════════
// THE STANDARD PAGE
// ═══════════════════════════════════════════════════════════════
function StandardPage() {
  return (
    <>
      <section style={{background:`linear-gradient(160deg,${C.navy},${C.navyLight})`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)",textAlign:"center"}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Public Knowledge Manual</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.white,marginBottom:14}}>The CredMark™ Standard</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:15,marginBottom:32}}>We publish our complete certification standard openly. Transparency is the foundation of trust.</p>
          <a href="https://novacert.in/CredMark-Public-Manual.pdf" target="_blank" rel="noopener noreferrer">
            <button className="cm-btn cm-btn-gold" style={{fontSize:15,padding:"14px 32px"}}><Icons.Download/> Download Full Manual (PDF)</button>
          </a>
        </div>
      </section>

      <Section>
        <div className="cm-grid-2" style={{marginBottom:48}}>
          {[
            {icon:"📋",title:"What's in the Manual",points:["The Five Pillars — complete criteria","Certification tiers and eligibility","The audit process step by step","Scoring methodology and thresholds","How to become an auditor","FAQ and glossary"]},
            {icon:"🔍",title:"Why We Publish It",points:["Transparency builds genuine trust","Anyone can study the standard","Aspiring auditors can prepare","Clients know exactly what was verified","Businesses know what to aim for","No hidden criteria or secret standards"]},
          ].map(c=>(
            <div key={c.title} className="cm-card" style={{padding:"28px 24px"}}>
              <div style={{fontSize:32,marginBottom:12}}>{c.icon}</div>
              <h3 className="cm-serif" style={{fontSize:22,fontWeight:700,color:C.navy,marginBottom:16}}>{c.title}</h3>
              {c.points.map(p=>(
                <div key={p} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:8,fontSize:13,color:C.body}}>
                  <span style={{color:C.gold,flexShrink:0,marginTop:1}}><Icons.Check/></span>{p}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Five Pillars Detail */}
        <h2 className="cm-serif" style={{fontSize:32,fontWeight:700,color:C.navy,marginBottom:8,textAlign:"center"}}>The Five Pillars — Complete Framework</h2>
        <p style={{textAlign:"center",color:C.muted,marginBottom:36,fontSize:15}}>Every CredMark™ evaluation is scored across these five dimensions</p>
        {[
          {n:"01",title:"Business Credibility",weight:"25%",min:"60%",criteria:["Minimum 1–2 years documented operational history","At least 2–3 verifiable, contactable past clients","Evidence of completed work — invoices, deliverables, projects","No history of fraudulent representation or fake credentials","Volume of work consistent with claimed experience"]},
          {n:"02",title:"Ethical Practices",weight:"—",min:"60%",criteria:["Transparent pricing — no hidden charges post-contract","Honest marketing — no misleading claims or false credentials","Fair contract terms with clear scope and deliverables","No evidence of review manipulation or fake testimonials","Honest communication in proposals and client dealings"]},
          {n:"03",title:"Operational Integrity",weight:"15%",min:"Pass/Fail",criteria:["Valid PAN, Aadhaar, or business registration document","GST or Udyam registration where applicable","Consistent business name across all platforms","Proper invoicing practices and financial transparency","Basic data handling and privacy practices with clients"]},
          {n:"04",title:"Client Trust",weight:"30%",min:"70%",criteria:["All references independently contactable by auditors","Structured reference interviews — not just a yes/no","At least one reference from work in past 18 months","Overall satisfaction score of 7/10 or above required","No unresolved consumer complaints at time of audit"]},
          {n:"05",title:"Professional Conduct",weight:"10%",min:"60%",criteria:["Cooperative and responsive throughout the audit process","Professional tone in all auditor communications","No abusive or unprofessional public conduct on any platform","Uses written agreements — not purely verbal commitments","Past complaints handled professionally and resolved"]},
        ].map((p,i)=>(
          <div key={p.n} className="cm-card" style={{padding:"28px 32px",marginBottom:16,borderLeft:`4px solid ${C.gold}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:14}}>
                <span className="cm-serif" style={{fontSize:32,fontWeight:700,color:`${C.gold}50`}}>{p.n}</span>
                <h3 style={{fontSize:18,fontWeight:700,color:C.navy}}>{p.title}</h3>
              </div>
              <div style={{display:"flex",gap:10}}>
                {p.weight!=="—"&&<span style={{fontSize:11,fontWeight:700,background:C.cream,border:`1px solid ${C.border}`,color:C.navy,padding:"3px 10px",borderRadius:20}}>Weight: {p.weight}</span>}
                <span style={{fontSize:11,fontWeight:700,background:`${C.goldLight}`,border:`1px solid ${C.gold}40`,color:C.navy,padding:"3px 10px",borderRadius:20}}>Min: {p.min}</span>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:8}}>
              {p.criteria.map(c=>(
                <div key={c} style={{display:"flex",gap:8,alignItems:"flex-start",fontSize:13,color:C.body}}>
                  <span style={{color:C.gold,flexShrink:0,marginTop:1}}><Icons.Check/></span>{c}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// GOVERNANCE PAGE
// ═══════════════════════════════════════════════════════════════
function GovernancePage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    const q = query(collection(db,"council_members"), where("published","==",true), orderBy("order","asc"));
    const unsub = onSnapshot(q, snap=>{
      setMembers(snap.docs.map(d=>({id:d.id,...d.data()})));
      setLoading(false);
    }, ()=>setLoading(false));
    return unsub;
  },[]);

  return (
    <>
      <section style={{background:`linear-gradient(160deg,${C.navy},${C.navyLight})`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)",textAlign:"center"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Oversight & Structure</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.white,marginBottom:14}}>Governance</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:15}}>Transparency in how CredMark™ is managed, reviewed, and updated.</p>
        </div>
      </section>

      <Section>
        {/* Governance structure cards */}
        <div className="cm-grid-2" style={{gap:24,marginBottom:48}}>
          {[
            {icon:"⚖️",title:"CredMark™ Standards Committee",desc:"An internal committee responsible for defining, reviewing, and updating the CredMark™ standard annually. Meets quarterly to review applicant feedback, industry developments, and necessary criteria updates. All changes are documented and versioned."},
            {icon:"👥",title:"Independent Advisory Council",desc:"A panel of independent professionals who provide external oversight of the CredMark™ standard. Members include experienced business, legal, and quality professionals who review the standard's integrity and ensure it remains objective, fair, and rigorous."},
            {icon:"📋",title:"Annual Standard Review",desc:"The CredMark™ standard is formally reviewed once every calendar year. Any changes to evaluation criteria, scoring weights, or process are published as a new version of the Public Knowledge Manual, available for download on this website."},
            {icon:"🔒",title:"Conflict of Interest Policy",desc:"All auditors must declare any existing or prior relationship with applicants they are assigned to evaluate. Any conflict results in automatic reassignment. NovaCert maintains a strict zero-tolerance policy on bias in the audit process."},
          ].map(c=>(
            <div key={c.title} className="cm-card" style={{padding:"28px 24px"}}>
              <div style={{fontSize:32,marginBottom:12}}>{c.icon}</div>
              <h3 className="cm-serif" style={{fontSize:20,fontWeight:700,color:C.navy,marginBottom:10}}>{c.title}</h3>
              <p style={{fontSize:13,color:C.muted,lineHeight:1.7}}>{c.desc}</p>
            </div>
          ))}
        </div>

        {/* ── ADVISORY COUNCIL MEMBERS ── */}
        <div style={{marginBottom:48}}>
          <div style={{textAlign:"center",marginBottom:36}}>
            <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:10}}>Independent Oversight</div>
            <h2 className="cm-serif" style={{fontSize:"clamp(22px,3vw,34px)",fontWeight:700,color:C.navy,marginBottom:10}}>Advisory Council</h2>
            <p style={{color:C.muted,fontSize:14,maxWidth:540,margin:"0 auto"}}>The CredMark™ Advisory Council provides independent oversight of the standard, ensuring it remains rigorous, fair, and credible.</p>
            <div style={{width:48,height:2,background:`linear-gradient(90deg,${C.gold},${C.goldBright})`,margin:"16px auto 0",borderRadius:1}}/>
          </div>

          {loading ? (
            <div style={{textAlign:"center",padding:40,color:C.muted}}><Icons.Loader/></div>
          ) : members.length === 0 ? (
            <div style={{textAlign:"center",padding:48,background:C.cream,borderRadius:4,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:36,marginBottom:12}}>👥</div>
              <p style={{color:C.muted,fontSize:14}}>Advisory Council members will be announced shortly.</p>
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:24}}>
              {members.map(m=>(
                <div key={m.id} className="cm-card" style={{padding:"28px 24px",textAlign:"center"}}>
                  {/* Photo */}
                  <div style={{width:90,height:90,borderRadius:"50%",margin:"0 auto 16px",overflow:"hidden",border:`3px solid ${C.gold}`,background:C.cream,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {m.photo
                      ? <img src={m.photo} alt={m.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                      : <span style={{fontSize:32}}>👤</span>
                    }
                  </div>
                  {/* Name */}
                  <h3 className="cm-serif" style={{fontSize:18,fontWeight:700,color:C.navy,marginBottom:4}}>{m.name}</h3>
                  {/* Designation */}
                  <p style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>{m.designation}</p>
                  {/* Organisation */}
                  {m.organisation && <p style={{fontSize:12,color:C.muted,marginBottom:12}}>{m.organisation}</p>}
                  {/* Divider */}
                  <div style={{width:32,height:1,background:C.border,margin:"12px auto"}}/>
                  {/* Bio */}
                  <p style={{fontSize:12,color:C.muted,lineHeight:1.7,marginBottom:12}}>{m.bio}</p>
                  {/* LinkedIn */}
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:12,color:C.teal,textDecoration:"none",fontWeight:600}}>
                      <Icons.Globe/> LinkedIn
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ownership */}
        <div style={{background:C.navy,borderRadius:4,padding:"40px",color:"#fff",marginBottom:40}}>
          <h2 className="cm-serif" style={{fontSize:28,fontWeight:700,color:C.gold,marginBottom:16}}>Ownership Structure</h2>
          <div className="cm-grid-2" style={{gap:32}}>
            <div>
              <h3 style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,.9)",marginBottom:12}}>CredMark™</h3>
              <p style={{fontSize:13,color:"rgba(255,255,255,.55)",lineHeight:1.7}}>CredMark™ is a proprietary certification standard owned exclusively by NovaCert. It is a framework — the rules and criteria against which businesses are evaluated.</p>
            </div>
            <div>
              <h3 style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,.9)",marginBottom:12}}>NovaCert</h3>
              <p style={{fontSize:13,color:"rgba(255,255,255,.55)",lineHeight:1.7}}>NovaCert is the operational platform that administers CredMark™. It manages all applications, conducts audits through its empanelled auditor panel, issues certificates, and maintains the public verification system.</p>
            </div>
          </div>
          <div style={{marginTop:28,padding:"16px 20px",background:`rgba(201,168,76,.1)`,border:`1px solid rgba(201,168,76,.2)`,borderRadius:3,fontSize:13,color:"rgba(255,255,255,.7)"}}>
            <strong style={{color:C.gold}}>In simple terms:</strong> CredMark™ defines the rules. NovaCert applies them.
          </div>
        </div>

        {/* Version history */}
        <h2 className="cm-serif" style={{fontSize:28,fontWeight:700,color:C.navy,marginBottom:20}}>Standard Version History</h2>
        <div className="cm-card" style={{overflow:"hidden"}}>
          {[["v1.0",`${new Date().getFullYear()}`,"Initial release of the CredMark™ standard. Five pillars framework, two certification tiers, 40–45 day audit methodology.","Current"]].map(([v,y,desc,status])=>(
            <div key={v} style={{display:"flex",gap:20,alignItems:"flex-start",padding:"20px 24px",borderBottom:`1px solid ${C.border}`}}>
              <div style={{flexShrink:0,textAlign:"center",minWidth:60}}>
                <div style={{fontWeight:800,fontSize:16,color:C.gold,fontFamily:"'Cormorant Garamond',serif"}}>{v}</div>
                <div style={{fontSize:12,color:C.muted}}>{y}</div>
              </div>
              <div style={{flex:1}}><p style={{fontSize:13,color:C.body,lineHeight:1.6}}>{desc}</p></div>
              <div style={{flexShrink:0}}><span style={{fontSize:11,fontWeight:700,background:`${C.goldLight}`,border:`1px solid ${C.gold}40`,color:C.gold,padding:"3px 10px",borderRadius:20}}>{status}</span></div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUDITORS PAGE
// ═══════════════════════════════════════════════════════════════
function AuditorsPage() {
  return (
    <>
      <section style={{background:`linear-gradient(160deg,${C.navy},${C.navyLight})`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)",textAlign:"center"}}>
        <div style={{maxWidth:680,margin:"0 auto"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Join the Panel</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.white,marginBottom:14}}>Become a CredMark™ Auditor</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:15,marginBottom:32}}>We are always looking for experienced professionals to join our independent panel of auditors and consultants.</p>
          <a href="https://novacert.in" target="_blank" rel="noopener noreferrer">
            <button className="cm-btn cm-btn-gold" style={{fontSize:15,padding:"14px 32px"}}>Apply at NovaCert.in <Icons.Arrow/></button>
          </a>
        </div>
      </section>

      <Section>
        <div className="cm-grid-2-wide" style={{marginBottom:56}}>
          <div>
            <h2 className="cm-serif" style={{fontSize:30,fontWeight:700,color:C.navy,marginBottom:20}}>What Does an Auditor Do?</h2>
            <p style={{fontSize:14,color:C.muted,lineHeight:1.8,marginBottom:16}}>A CredMark™ auditor independently evaluates certification applications against the published standard. Their role is to be the impartial, evidence-based decision-maker.</p>
            {["Review all submitted documents for authenticity","Conduct independent client reference interviews","Perform online presence and social proof audit","Score applications across all five credibility pillars","Submit a structured audit report with evidence","Maintain complete confidentiality at all times"].map(r=>(
              <div key={r} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:10,fontSize:13,color:C.body}}>
                <span style={{color:C.gold,flexShrink:0,marginTop:1}}><Icons.Check/></span>{r}
              </div>
            ))}
          </div>
          <div>
            <h2 className="cm-serif" style={{fontSize:30,fontWeight:700,color:C.navy,marginBottom:20}}>Who Can Apply?</h2>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {[
                {icon:"🎓",title:"5+ Years Experience",desc:"In business, finance, quality management, compliance, or related fields"},
                {icon:"🇮🇳",title:"India Knowledge",desc:"Strong understanding of the Indian MSME and service sector landscape"},
                {icon:"⚖️",title:"Impartiality",desc:"No conflict of interest with applicants you are assigned to evaluate"},
                {icon:"🤝",title:"Availability",desc:"10–15 hours available per audit assignment on a freelance basis"},
              ].map(r=>(
                <div key={r.title} className="cm-card" style={{padding:"16px 20px",display:"flex",gap:14,alignItems:"flex-start"}}>
                  <span style={{fontSize:22,flexShrink:0}}>{r.icon}</span>
                  <div>
                    <p style={{fontWeight:700,fontSize:13,color:C.navy,marginBottom:2}}>{r.title}</p>
                    <p style={{fontSize:12,color:C.muted}}>{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Code of Conduct */}
        <div style={{background:C.cream,borderRadius:4,padding:"40px",border:`1px solid ${C.border}`,marginBottom:40}}>
          <h2 className="cm-serif" style={{fontSize:28,fontWeight:700,color:C.navy,marginBottom:24,textAlign:"center"}}>The Auditor Code of Conduct</h2>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:20}}>
            {[["⚖️","Objectivity","Decisions based solely on evidence. No personal opinions or biases."],["🔒","Confidentiality","All applicant information is strictly confidential at all times."],["📋","Transparency","Every score must be backed by documented, verifiable evidence."],["⏰","Timeliness","Audits completed within the agreed 40–45 day window without exception."],["🚫","Integrity","No gifts, payments, or favours from applicants. Zero tolerance."]].map(([icon,title,desc])=>(
              <div key={title} style={{textAlign:"center"}}>
                <div style={{fontSize:28,marginBottom:8}}>{icon}</div>
                <h3 style={{fontSize:14,fontWeight:700,color:C.navy,marginBottom:6}}>{title}</h3>
                <p style={{fontSize:12,color:C.muted,lineHeight:1.6}}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{background:`linear-gradient(135deg,${C.navy},${C.navyLight})`,borderRadius:4,padding:"40px",textAlign:"center",color:"#fff"}}>
          <h3 className="cm-serif" style={{fontSize:26,fontWeight:700,color:C.gold,marginBottom:10}}>Ready to Apply?</h3>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:14,marginBottom:24,maxWidth:480,margin:"0 auto 24px"}}>Submit your empanelment application at NovaCert.in. Our team reviews every application and shortlisted candidates are contacted within 5–7 working days.</p>
          <a href="https://novacert.in" target="_blank" rel="noopener noreferrer">
            <button className="cm-btn cm-btn-gold" style={{fontSize:15,padding:"14px 32px"}}>Apply at NovaCert.in <Icons.Arrow/></button>
          </a>
        </div>
      </Section>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONTACT PAGE
// ═══════════════════════════════════════════════════════════════
function ContactPage() {
  return (
    <>
      <section style={{background:`linear-gradient(160deg,${C.navy},${C.navyLight})`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)",textAlign:"center"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Get in Touch</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(28px,4vw,50px)",fontWeight:700,color:C.white,marginBottom:14}}>Contact CredMark™</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:15}}>Use the appropriate contact for your query. We respond within 3 working days.</p>
        </div>
      </section>

      <Section style={{maxWidth:800}}>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {[
            {icon:"📧",label:"Standards Enquiries",email:"standards@credmark.org",desc:"Questions about the CredMark™ standard, criteria, or evaluation methodology"},
            {icon:"🔍",label:"Certificate Verification",email:"verify@credmark.org",desc:"Disputes, certificate verification issues, or suspected misuse of the CredMark™ badge"},
            {icon:"👥",label:"Auditor Applications",email:"auditors@credmark.org",desc:"Interest in joining the NovaCert empanelled auditor or consultant panel"},
            {icon:"🏢",label:"General & Business",email:"info@novacert.in",desc:"Certification applications, pricing, and all general business enquiries via NovaCert"},
          ].map(c=>(
            <div key={c.label} className="cm-card" style={{padding:"24px 28px",display:"flex",gap:20,alignItems:"flex-start"}}>
              <div style={{fontSize:28,lineHeight:1,flexShrink:0}}>{c.icon}</div>
              <div style={{flex:1}}>
                <p style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>{c.label}</p>
                <a href={`mailto:${c.email}`} style={{fontSize:16,fontWeight:700,color:C.navy,textDecoration:"none",display:"block",marginBottom:4}}>{c.email}</a>
                <p style={{fontSize:13,color:C.muted}}>{c.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{marginTop:32,background:C.cream,borderRadius:4,padding:"28px 32px",border:`1px solid ${C.border}`,textAlign:"center"}}>
          <p style={{fontSize:14,color:C.muted,marginBottom:4}}>For certification applications, visit</p>
          <a href="https://novacert.in" target="_blank" rel="noopener noreferrer" style={{fontSize:18,fontWeight:700,color:C.teal,textDecoration:"none",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}><Icons.Globe/> novacert.in</a>
          <p style={{fontSize:13,color:C.muted,marginTop:8}}>📞 +91 91150 19666 · Mon–Sat, 10am–6pm IST</p>
        </div>
      </Section>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// ADMIN LOGIN
// ═══════════════════════════════════════════════════════════════
function AdminLogin({ authChecked }) {
  const [email, setEmail] = useState("");
  const [pass,  setPass]  = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const login = async () => {
    if(!email||!pass) return;
    setLoading(true); setError("");
    try {
      await signInWithEmailAndPassword(auth, email, pass);
    } catch(err) {
      setError("Invalid email or password.");
    }
    setLoading(false);
  };

  if(!authChecked) return <div style={{textAlign:"center",padding:100,color:C.muted}}>Loading...</div>;

  return (
    <section style={{maxWidth:400,margin:"0 auto",padding:"80px 24px",textAlign:"center"}}>
      <div style={{width:56,height:56,borderRadius:4,background:C.navy,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",color:C.gold,fontSize:24}}>⚖️</div>
      <h2 className="cm-serif" style={{fontSize:28,fontWeight:700,color:C.navy,marginBottom:6}}>Admin Access</h2>
      <p style={{color:C.muted,fontSize:13,marginBottom:24}}>CredMark™ Admin Panel</p>
      <input className="cm-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Admin email" style={{marginBottom:12,textAlign:"center"}}/>
      <input className="cm-input" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="Password" onKeyDown={e=>e.key==="Enter"&&login()} style={{marginBottom:12,textAlign:"center"}}/>
      {error && <p style={{color:"#DC2626",fontSize:13,marginBottom:12}}>{error}</p>}
      <button className="cm-btn cm-btn-navy" onClick={login} disabled={loading} style={{width:"100%",justifyContent:"center"}}>
        {loading ? "Logging in..." : "Login"}
      </button>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// ADMIN PANEL — Council Member Manager
// ═══════════════════════════════════════════════════════════════
function AdminPanel() {
  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editing,  setEditing]  = useState(null); // null=list, "new"=new, {...}=edit
  const [saving,   setSaving]   = useState(false);
  const [uploading,setUploading]= useState(false);
  const [deleting, setDeleting] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile,    setPhotoFile]    = useState(null);

  const BLANK = { name:"", designation:"", organisation:"", bio:"", linkedin:"", photo:"", published:false, order:99 };
  const [form, setForm] = useState(BLANK);

  useEffect(()=>{
    const q = query(collection(db,"council_members"), orderBy("order","asc"));
    const unsub = onSnapshot(q, snap=>{
      setMembers(snap.docs.map(d=>({id:d.id,...d.data()})));
      setLoading(false);
    });
    return unsub;
  },[]);

  const u = useCallback((field)=>(e)=>{
    const val = e.target.type==="checkbox" ? e.target.checked : e.target.value;
    setForm(prev=>({...prev,[field]:val}));
  },[]);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if(!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const openNew  = () => { setForm(BLANK); setPhotoFile(null); setPhotoPreview(null); setEditing("new"); };
  const openEdit = (m)  => { setForm({...BLANK,...m}); setPhotoFile(null); setPhotoPreview(m.photo||null); setEditing(m); };
  const cancel   = ()   => { setEditing(null); setForm(BLANK); setPhotoFile(null); setPhotoPreview(null); };

  const save = async () => {
    if(!form.name.trim()) { alert("Name is required."); return; }
    setSaving(true);
    try {
      let photoUrl = form.photo || "";

      // Upload new photo if selected
      if(photoFile) {
        setUploading(true);
        const ts = Date.now();
        const storageRef = ref(storage, `council/${ts}_${photoFile.name.replace(/[^a-zA-Z0-9._-]/g,"_")}`);
        await uploadBytes(storageRef, photoFile);
        photoUrl = await getDownloadURL(storageRef);
        setUploading(false);
      }

      const data = { ...form, photo: photoUrl, order: Number(form.order)||99 };

      if(editing === "new") {
        await addDoc(collection(db,"council_members"), data);
      } else {
        await updateDoc(doc(db,"council_members", editing.id), data);
      }
      cancel();
    } catch(err) {
      alert("Save failed: " + err.message);
    }
    setSaving(false);
  };

  const togglePublish = async (m) => {
    await updateDoc(doc(db,"council_members",m.id), {published:!m.published});
  };

  const deleteMember = async (id) => {
    if(!window.confirm("Delete this member permanently?")) return;
    setDeleting(id);
    try { await deleteDoc(doc(db,"council_members",id)); }
    catch(err) { alert("Delete failed: "+err.message); }
    setDeleting(null);
  };

  // ── EDITOR ──
  if(editing !== null) return (
    <section style={{maxWidth:700,margin:"0 auto",padding:"48px 24px"}}>
      <button onClick={cancel} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:14,fontFamily:"'Inter',sans-serif",marginBottom:20}}>← Back</button>
      <h2 className="cm-serif" style={{fontSize:28,fontWeight:700,color:C.navy,marginBottom:28}}>{editing==="new"?"Add Council Member":"Edit Member"}</h2>

      <div className="cm-card" style={{padding:"32px 28px"}}>

        {/* Photo upload */}
        <div style={{marginBottom:20}}>
          <label className="cm-label">Profile Photo</label>
          <div style={{display:"flex",alignItems:"center",gap:16,marginTop:6}}>
            <div style={{width:80,height:80,borderRadius:"50%",border:`3px solid ${C.gold}`,overflow:"hidden",background:C.cream,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {photoPreview ? <img src={photoPreview} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:28}}>👤</span>}
            </div>
            <label style={{flex:1,border:`2px dashed ${C.border}`,borderRadius:4,padding:"12px 16px",cursor:"pointer",background:C.cream,textAlign:"center",fontSize:13,color:C.muted,fontWeight:500}}
              onMouseEnter={e=>e.currentTarget.style.borderColor=C.gold}
              onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
              <input type="file" accept="image/*" onChange={handlePhoto} style={{display:"none"}}/>
              {photoFile ? `✓ ${photoFile.name}` : "Click to upload photo (JPG / PNG)"}
            </label>
          </div>
          {/* Or paste URL */}
          <div style={{marginTop:8}}>
            <input className="cm-input" value={form.photo} onChange={u("photo")} placeholder="Or paste a photo URL directly..." style={{fontSize:12}}/>
          </div>
        </div>

        {/* Fields */}
        {[
          {label:"Full Name *",        field:"name",         placeholder:"Dr. / Mr. / Ms. Full Name"},
          {label:"Designation *",      field:"designation",  placeholder:"e.g. Former Joint Secretary, Ministry of MSME"},
          {label:"Organisation",       field:"organisation", placeholder:"e.g. Government of India / IIM Ahmedabad"},
          {label:"LinkedIn URL",       field:"linkedin",     placeholder:"https://linkedin.com/in/..."},
        ].map(({label,field,placeholder})=>(
          <div key={field} style={{marginBottom:14}}>
            <label className="cm-label">{label}</label>
            <input className="cm-input" value={form[field]} onChange={u(field)} placeholder={placeholder}/>
          </div>
        ))}

        <div style={{marginBottom:14}}>
          <label className="cm-label">Bio (2–3 sentences) *</label>
          <textarea className="cm-input" rows={3} value={form.bio} onChange={u("bio")} placeholder="Brief professional background — qualifications, experience, why they're on the council..." style={{resize:"vertical"}}/>
        </div>

        <div style={{marginBottom:20}}>
          <label className="cm-label">Display Order (lower = appears first)</label>
          <input className="cm-input" type="number" value={form.order} onChange={u("order")} placeholder="1, 2, 3..." style={{width:120}}/>
        </div>

        {/* Publish toggle */}
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",padding:"14px 16px",background:form.published?`${C.goldLight}`:"#F9FAFB",borderRadius:4,border:`2px solid ${form.published?C.gold:C.border}`,marginBottom:24}}>
          <input type="checkbox" checked={form.published} onChange={u("published")} style={{width:18,height:18,accentColor:C.gold}}/>
          <div>
            <p style={{fontWeight:700,fontSize:14,color:form.published?C.navy:C.muted}}>{form.published?"✅ Published — visible on Governance page":"📝 Hidden — not visible publicly"}</p>
            <p style={{fontSize:12,color:C.muted,marginTop:2}}>You can save as hidden and publish later</p>
          </div>
        </label>

        <div style={{display:"flex",gap:12}}>
          <button className="cm-btn cm-btn-gold" disabled={saving} onClick={save} style={{minWidth:140,justifyContent:"center"}}>
            {saving ? (uploading?"Uploading photo...":"Saving...") : (form.published?"Save & Publish":"Save as Hidden")}
          </button>
          <button className="cm-btn cm-btn-outline" onClick={cancel}>Cancel</button>
        </div>
      </div>
    </section>
  );

  // ── LIST VIEW ──
  return (
    <section style={{maxWidth:900,margin:"0 auto",padding:"48px 24px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:32,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 className="cm-serif" style={{fontSize:32,fontWeight:700,color:C.navy,marginBottom:4}}>Advisory Council Manager</h1>
          <p style={{fontSize:13,color:C.muted}}>{members.filter(m=>m.published).length} published · {members.filter(m=>!m.published).length} hidden</p>
        </div>
        <button className="cm-btn cm-btn-gold" onClick={openNew}>+ Add Member</button>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:60}}><Icons.Loader/></div>
      ) : members.length === 0 ? (
        <div className="cm-card" style={{padding:64,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>👥</div>
          <h3 className="cm-serif" style={{fontSize:22,marginBottom:8,color:C.navy}}>No members yet</h3>
          <p style={{color:C.muted,fontSize:14,marginBottom:24}}>Add your first Advisory Council member to showcase them on the Governance page.</p>
          <button className="cm-btn cm-btn-gold" onClick={openNew}>Add First Member</button>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {members.map(m=>(
            <div key={m.id} className="cm-card" style={{padding:"18px 24px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              {/* Photo */}
              <div style={{width:52,height:52,borderRadius:"50%",border:`2px solid ${C.gold}`,overflow:"hidden",background:C.cream,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                {m.photo ? <img src={m.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:20}}>👤</span>}
              </div>
              {/* Info */}
              <div style={{flex:1,minWidth:180}}>
                <h3 style={{fontSize:15,fontWeight:700,color:C.navy,marginBottom:2}}>{m.name}</h3>
                <p style={{fontSize:12,color:C.muted}}>{m.designation}{m.organisation?` · ${m.organisation}`:""}</p>
              </div>
              {/* Order */}
              <div style={{fontSize:11,color:C.muted,background:C.cream,padding:"3px 10px",borderRadius:12,fontWeight:600}}>#{m.order}</div>
              {/* Status + Actions */}
              <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                <span style={{fontSize:11,fontWeight:700,padding:"4px 10px",borderRadius:12,background:m.published?`${C.goldLight}`:"#F5F5F4",color:m.published?C.navy:C.muted}}>
                  {m.published?"● Live":"○ Hidden"}
                </span>
                <button onClick={()=>togglePublish(m)} style={{padding:"6px 12px",fontSize:12,fontWeight:600,border:`1px solid ${C.border}`,borderRadius:4,background:"#fff",cursor:"pointer",fontFamily:"'Inter',sans-serif",color:C.body}}>
                  {m.published?"Hide":"Publish"}
                </button>
                <button onClick={()=>openEdit(m)} style={{padding:"6px 12px",fontSize:12,fontWeight:600,border:`1px solid ${C.gold}`,borderRadius:4,background:"#fff",color:C.gold,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Edit
                </button>
                <button onClick={()=>deleteMember(m.id)} disabled={deleting===m.id} style={{padding:"6px 10px",fontSize:12,border:"1px solid #FCA5A5",borderRadius:4,background:"#fff",color:"#DC2626",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  {deleting===m.id?"...":"🗑"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info box */}
      <div style={{marginTop:24,padding:"16px 20px",background:`${C.goldLight}`,border:`1px solid ${C.gold}40`,borderRadius:4,fontSize:13,color:C.navy}}>
        <strong>💡 How it works:</strong> Published members appear live on the <strong>Governance page</strong> of credmark.org. Use the order number to control who appears first. Photos can be uploaded or linked via URL.
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════════════════════════
// TERMS OF USE PAGE — CredMark
// ═══════════════════════════════════════════════════════════════
function CredMarkTermsPage() {
  const Sec = ({ title, children }) => (
    <div style={{marginBottom:36}}>
      <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:20,color:"#0F1F3D",paddingBottom:8,borderBottom:`2px solid #F5E6C0`,marginBottom:16}}>{title}</h2>
      {children}
    </div>
  );
  const P = ({children}) => <p style={{fontSize:14,color:"#374151",lineHeight:1.8,marginBottom:12}}>{children}</p>;
  const Li = ({children}) => <p style={{fontSize:14,color:"#374151",lineHeight:1.8,marginBottom:8,marginLeft:16}}>•  {children}</p>;

  return (
    <>
      <section style={{background:`linear-gradient(160deg,#0F1F3D,#1E3A5F)`,padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,48px)",textAlign:"center"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#C9A84C",textTransform:"uppercase",letterSpacing:2,marginBottom:12}}>Legal</div>
          <h1 className="cm-serif" style={{fontSize:"clamp(26px,4vw,44px)",fontWeight:700,color:"#fff",marginBottom:12}}>Website Terms of Use</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:14}}>novacert.in and credmark.org &nbsp;·&nbsp; Last updated: 29 March 2026</p>
        </div>
      </section>

      <section style={{maxWidth:820,margin:"0 auto",padding:"56px 24px"}}>

        <div style={{background:"#F5E6C0",border:"1px solid #C9A84C",borderRadius:4,padding:"16px 20px",marginBottom:40,fontSize:14,color:"#0F1F3D"}}>
          These Terms of Use govern your access to and use of <strong>novacert.in</strong> and <strong>credmark.org</strong>, operated by NovaCert. By using these websites you agree to these Terms.
        </div>

        <Sec title="1. Intellectual Property Rights">
          <P>All content on these websites — including text, graphics, logos, images, certification standards documentation, scoring frameworks, evaluation criteria, and software — is the property of NovaCert and is protected under the Copyright Act, 1957 and applicable intellectual property laws.</P>
          <P>The <strong>CredMark™</strong> name, logo, certification badge, and associated branding are trademarks of NovaCert (trademark applications filed under the Trade Marks Act, 1999). The <strong>NovaCert</strong> name, logo, and branding are trademarks of NovaCert. Unauthorised use is strictly prohibited.</P>
          <P>No content from these websites may be reproduced, distributed, modified, displayed, or transmitted in any form without the prior written permission of NovaCert.</P>
        </Sec>

        <Sec title="2. Restrictions on Use">
          <P>You shall not:</P>
          {["Copy, reproduce, or distribute any content from the websites",
            "Reverse engineer or attempt to derive the CredMark™ evaluation methodology, scoring algorithms, or proprietary processes",
            "Use any automated tool, scraper, bot, or data mining technique to extract data",
            "Frame, mirror, or deep-link to any portion of the websites without written permission",
            "Use any content to train artificial intelligence or machine learning models",
            "Use the CredMark™ badge or NovaCert logo without a valid certification and written licence",
            "Create derivative works based on the CredMark™ standard or certification criteria",
            "Use the websites for any unlawful purpose or in violation of applicable law",
          ].map((t,i) => <Li key={i}>{t}</Li>)}
        </Sec>

        <Sec title="3. Certification Badge Usage">
          <P>The CredMark™ certification badge and NovaCert Verified badge may only be used by businesses holding a valid, current certification issued by NovaCert. Unauthorised use constitutes trademark infringement and will be prosecuted to the fullest extent under Indian law. Upon expiry, suspension, or revocation of certification, all badge usage must cease immediately.</P>
        </Sec>

        <Sec title="4. Certificate Verification">
          <P>The online certificate verification service is provided for informational purposes only. Verification results reflect the certification status at the time of query. NovaCert makes no guarantee regarding the future performance of certified businesses. Users should exercise their own judgment and due diligence in addition to relying on certification status.</P>
        </Sec>

        <Sec title="5. Disclaimer of Warranties">
          <P>The websites and all content are provided "as is" and "as available" without any warranties, express or implied. NovaCert does not warrant that the websites will be uninterrupted or error-free. Certification represents compliance with the CredMark™ standard at the time of assessment only and does not guarantee future performance.</P>
        </Sec>

        <Sec title="6. Limitation of Liability">
          <P>To the maximum extent permitted by law, NovaCert shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the websites. NovaCert shall not be liable for any business decisions made in reliance on certification status. NovaCert's total liability shall not exceed the amount paid by you to NovaCert (if any) in the preceding 12 months.</P>
        </Sec>

        <Sec title="7. Privacy and Data Protection">
          <P>NovaCert collects and processes personal data in accordance with applicable Indian data protection laws, including the Digital Personal Data Protection Act, 2023. By submitting information through these websites, you consent to the collection, storage, and processing of your personal data for certification purposes. NovaCert implements reasonable security measures to protect personal data as required under Section 43A of the Information Technology Act, 2000.</P>
          <P>For privacy-related enquiries: <a href="mailto:info@novacert.in" style={{color:"#C9A84C"}}>info@novacert.in</a></P>
        </Sec>

        <Sec title="8. Grievance Officer">
          <P>In compliance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021:</P>
          <div style={{background:"#FAF8F2",border:`1px solid #E5E0D5`,borderRadius:4,padding:"16px 20px",fontSize:14,color:"#374151"}}>
            <p style={{marginBottom:6}}><strong>Grievance Officer:</strong> Proprietor, NovaCert</p>
            <p style={{marginBottom:6}}><strong>Email:</strong> <a href="mailto:info@novacert.in" style={{color:"#C9A84C"}}>info@novacert.in</a></p>
            <p style={{marginBottom:6}}><strong>Phone:</strong> +91 91150 19666</p>
            <p style={{marginBottom:6}}><strong>Address:</strong> 538K/449, Triveni Nagar Colony II, Sitapur Road, Lucknow, Uttar Pradesh – 226020</p>
            <p style={{marginBottom:0,color:"#6B7280",fontSize:13}}>Complaints acknowledged within 24 hours and resolved within 15 days.</p>
          </div>
        </Sec>

        <Sec title="9. Governing Law and Jurisdiction">
          <P>These Terms shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Lucknow, Uttar Pradesh.</P>
        </Sec>

        <Sec title="10. Changes to These Terms">
          <P>NovaCert reserves the right to modify these Terms at any time. Changes will be posted on the websites with the updated "Last updated" date. Continued use after changes constitutes acceptance.</P>
        </Sec>

        <Sec title="11. Contact">
          <P>For any questions about these Terms: <a href="mailto:info@novacert.in" style={{color:"#C9A84C"}}>info@novacert.in</a> · +91 91150 19666 · 538K/449, Triveni Nagar Colony II, Sitapur Road, Lucknow, Uttar Pradesh – 226020</P>
        </Sec>

        <div style={{marginTop:48,padding:"20px 24px",background:"#0F1F3D",borderRadius:4,textAlign:"center"}}>
          <p style={{fontSize:13,color:"rgba(255,255,255,.4)"}}>© {new Date().getFullYear()} CredMark™. All rights reserved. &nbsp;·&nbsp; novacert.in &nbsp;·&nbsp; credmark.org</p>
        </div>

      </section>
    </>
  );
}
